import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Move,
  Mover,
  Signature,
  Stop,
  Agreement,
  PaymentLink,
  Charge,
} from "@/types";
import { baseApi } from "@/store/baseApi";
import { Button } from "@/components/ui/button";
import SignatureCanvas from "react-signature-canvas";
import RateCharges from "@/pages/Moves/components/RateCharges";
// import { useGetMoveQuery } from "@/store/moves/apis";
import {
  BoxIcon,
  CalendarIcon,
  ClipboardIcon,
  Clock,
  MapIcon,
  TruckIcon,
  PackageIcon,
  ArrowRightIcon,
} from "lucide-react";
import {
  cn,
  formatAddress,
  formatService,
  formatStartWindow,
} from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuoteLeft } from "@fortawesome/free-solid-svg-icons";
import { useSignAgreementMutation } from "@/store/agreements/apis";
import { parseISO, format } from "date-fns";
import SignatureDisplay from "@/components/SignatureDisplay";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { CHARGE_PURPOSES } from "@/constants";
import { PortalHeader } from "./PortalHeader";
import PhotoUploadSection from "./PhotoUploadSection";
import { isLead } from "../EditMove/functions";

type Response = {
  move: Move;
  mover: Mover;
  agreements: Agreement[];
  payment_links: PaymentLink[];
};

const moveApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMoveByDigest: builder.query<Response, string>({
      query: (digest) => `/customer-hub/move/${digest}`,
    }),
  }),
});

const { useGetMoveByDigestQuery } = moveApi;

export default function CustomerPortal() {
  const { digest } = useParams<{ digest: string }>();
  const { data, isLoading } = useGetMoveByDigestQuery(digest || "");

  const move = data?.move;
  const mover = data?.mover;
  const agreements = data?.agreements;
  const paymentLinks = move?.payment_links;
  const customer_photos = move?.media?.filter(
    (media) => !media.uploader_id || media.uploader_id === move?.customer_id
  );
  const unPaidDepositLink = mover?.mover_configuration?.deposit_link
    ? ({ full_url: mover.mover_configuration.deposit_link } as PaymentLink)
    : paymentLinks?.find((link) => link.purpose === "deposit" && !link.paid);

  const signature = agreements
    ?.find(
      (agreement: Agreement) =>
        agreement.kind === "estimate" && !agreement.voided_at
    )
    ?.signatures?.find((s: Signature) => s.signed_by === "customer");

  useEffect(() => {
    if (mover) {
      document.title = `Customer Hub | ${mover.name}`;
    }
  }, [mover]);

  const salesPitch = () => {
    return (
      <div className="w-full md:w-[600px]">
        <p className="font-semibold">
          Thank you for your interest in moving with us! We'd love to help make
          your move as smooth and stress-free as possible.
        </p>
      </div>
    );
  };

  const showReserveCopy = () => {
    if (mover?.business_type !== "mover") return null;

    return (
      <div className="w-full md:w-[600px]">
        <p className="text-red-600 font-medium">
          This estimate is based on the information provided.
          <br />
          Dates can often book up quickly--to reserve your service and spot on
          our calendar, please call us at {mover?.business_phone} at the
          earliest.
        </p>
      </div>
    );
  };

  if (isLoading || !move || !mover) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div
      className="min-h-screen py-12"
      style={{
        backgroundColor: mover.mover_configuration.brand_color_hex
          ? `#${mover.mover_configuration.brand_color_hex}`
          : "red",
      }}
    >
      <div className="max-w-screen-lg mx-auto bg-stone-50 border border-stone-300 p-4 flex flex-col gap-4">
        <PortalHeader mover={mover} className="border-b border-stone-500" />
        <div className="flex flex-row items-center gap-6 max-w-screen overflow-x-auto">
          <a
            href="#service"
            className="text-sm font-normal font-mono underline underline-offset-4"
          >
            Details
          </a>
          <a
            href="#inventory"
            className="text-sm font-normal font-mono underline underline-offset-4"
          >
            Inventory
          </a>
          <a
            href="#billing"
            className="text-sm font-normal font-mono underline underline-offset-4"
          >
            Charges
          </a>
          <a
            href="#payments"
            className="text-sm font-normal font-mono underline underline-offset-4"
          >
            Payments
          </a>
          <a
            href="#documents"
            className="text-sm font-normal font-mono underline underline-offset-4"
          >
            Documents
          </a>
          <a
            href="#terms"
            className="text-sm font-normal font-mono underline underline-offset-4"
          >
            Terms and Conditions
          </a>
        </div>
        <div className="w-full flex flex-row justify-between items-start text-center gap-12">
          <div className="flex flex-col gap-2 items-start">
            <h2 className="text-2xl font-bold">{move.customer_name}</h2>
            <div className="flex flex-col items-start gap-1">
              <p className="text-sm font-normal font-mono">#{move.number}</p>
              <div className="text-sm font-normal font-mono">
                {move.customer_mobile_phone}
              </div>
              <div className="text-sm font-normal font-mono">
                {move.customer_email}
              </div>
            </div>
          </div>
        </div>

        {mover.business_type === "mover" && isLead(move) && salesPitch()}

        {move.notes_personal_message && (
          <div className="w-full md:w-[600px] bg-amber-50 relative">
            <div className="absolute -top-3 -left-2">
              <FontAwesomeIcon icon={faQuoteLeft} className="w-4 h-4" />
            </div>
            <div
              className="text-sm p-4 border border-dashed border-gray-300 rounded-md"
              dangerouslySetInnerHTML={{
                __html:
                  move.notes_personal_message?.replace(/\n/g, "<br>") || "",
              }}
            />
          </div>
        )}

        <ServiceDetailsArea move={move} />

        <DepositPaymentArea
          move={move}
          mover={mover}
          unPaidDepositLink={unPaidDepositLink}
          signature={signature}
        />

        <InventoryArea move={move} mover={mover} />
        <BillingArea move={move} mover={mover} />
        <PaymentsArea move={move} />
        <SignatureArea move={move} mover={mover} agreements={agreements} />

        {!mover.mover_configuration.signed_estimates &&
          move.status === "quoted" &&
          showReserveCopy()}

        <PhotoUploadSection
          moveDigest={digest || ""}
          photos={customer_photos || []}
        />

        <DocumentArea mover={mover} />
        <TermsAndConditionsArea mover={mover} />
      </div>
    </div>
  );
}

const TermsAndConditionsArea = ({ mover }: { mover: Mover }) => {
  if (mover.business_type !== "mover") return null;
  if (
    !mover.mover_configuration.terms_and_conditions ||
    mover.mover_configuration.terms_and_conditions.trim().length === 0
  )
    return null;

  return (
    <div id="terms" className="p-4 bg-white">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-medium">Terms and Conditions</h2>
        <div className="text-sm font-normal font-mono">
          {mover.mover_configuration.terms_and_conditions}
        </div>
      </div>
    </div>
  );
};

const DepositPaymentArea = ({
  move,
  mover,
  unPaidDepositLink,
  signature,
}: {
  move: Move;
  mover: Mover;
  unPaidDepositLink: PaymentLink | undefined;
  signature: Signature | undefined;
}) => {
  if (!mover?.mover_configuration.enabled_for_payments) return null;
  if (!move?.collect_deposit) return null;
  if (!move?.deposit_amount || move?.deposit_amount === 0) return null;
  if (!unPaidDepositLink || !signature) return null;

  return (
    <div className="p-4 bg-white flex flex-col gap-4">
      <h2 className="text-lg font-medium">Deposit</h2>
      <p className="mb-4 font-medium text-amber-700">
        You have a deposit due on this move. Reserve your date by paying the
        deposit via the link below.
      </p>
      <Button
        // className="bg-green-700 hover:bg-green-600 text-white text-base"
        onClick={() => window.open(unPaidDepositLink.full_url, "_blank")}
        size="lg"
        variant="default"
      >
        Pay Deposit &mdash;{" "}
        {Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
        }).format(move.deposit_amount)}
        &nbsp;
        <ArrowRightIcon className="w-4 h-4" />
      </Button>
    </div>
  );
};

const ServiceDetailsArea = ({ move }: { move: Move }) => {
  const showAttribute = (
    icon: React.ReactNode,
    label: string,
    value: string
  ) => {
    return (
      <div className="w-full flex flex-row items-baseline gap-4 space-y-1">
        <div className="w-[140px] flex flex-row items-center gap-0.5 font-mono uppercase text-xs text-gray-700">
          {icon}
          {label}
        </div>
        <div className="flex-1 text-sm font-normal">{value}</div>
      </div>
    );
  };

  return (
    <div id="service" className="p-4 bg-white flex flex-col gap-4">
      <h2 className="text-lg font-medium">Service Details</h2>
      <div className="flex flex-col gap-1">
        {showAttribute(
          <CalendarIcon className="w-3 h-3" />,
          "Service Date",
          move.service_date
            ? format(parseISO(move.service_date), "MM/dd/yyyy")
            : "-"
        )}
        {showAttribute(
          <Clock className="w-3 h-3" />,
          "Arrival",
          formatStartWindow(
            move.start_window_begin_hour,
            move.start_window_begin_minute,
            move.start_window_begin_meridiem
          ) +
            " to " +
            formatStartWindow(
              move.start_window_end_hour,
              move.start_window_end_minute,
              move.start_window_end_meridiem
            )
        )}

        {showAttribute(
          <BoxIcon className="w-3 h-3" />,
          "Service",
          formatService(move)
        )}

        {showAttribute(
          <MapIcon className="w-3 h-3" />,
          "Origin",
          formatAddress(move.origin, true)
        )}

        {move.stops?.map((stop: Stop) =>
          showAttribute(
            <MapIcon className="w-3 h-3" />,
            `Stop`,
            formatAddress(stop, true)
          )
        )}

        {move.destination?.street &&
          showAttribute(
            <MapIcon className="w-3 h-3" />,
            "Destination",
            formatAddress(move.destination, true)
          )}

        {(move.num_of_movers || move.num_of_trucks) &&
          showAttribute(
            <TruckIcon className="w-3 h-3" />,
            "Crew/Trucks",
            `${move.num_of_movers || "n/a"} movers, ${
              move.num_of_trucks || "n/a"
            } truck(s)`
          )}

        {showAttribute(
          <ClipboardIcon className="w-3 h-3" />,
          "Notes",
          move.notes_customer
        )}
      </div>
    </div>
  );
};

const PaymentsArea = ({ move }: { move: Move }) => {
  if (move.charges.length === 0) return null;

  return (
    <div id="payments" className="p-4 bg-white flex flex-col gap-4">
      <h2 className="text-lg font-medium">Payments</h2>
      <div className="w-full md:w-[600px]">
        <Table>
          <TableBody>
            {[...move.charges]
              .filter(
                (charge) =>
                  charge.status !== "pending" &&
                  charge.status !== "voided" &&
                  charge.status !== "failed"
              )
              .sort(
                (a, b) =>
                  parseISO(b.created_at).getTime() -
                  parseISO(a.created_at).getTime()
              )
              .map((charge: Charge) => (
                <TableRow key={charge.id}>
                  <TableCell className="pl-0">
                    {charge.kind === "refund" && "("}
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(Math.abs(charge.amount))}
                    {charge.kind === "refund" && ")"}
                  </TableCell>
                  <TableCell>
                    {charge.kind === "refund"
                      ? "Refund"
                      : CHARGE_PURPOSES[
                          charge.purpose as keyof typeof CHARGE_PURPOSES
                        ]}
                  </TableCell>
                  <TableCell>
                    Paid on{" "}
                    {format(parseISO(charge.created_at), "MM/dd hh:mm a XXX")}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const DocumentArea = ({ mover }: { mover: Mover }) => {
  if (mover.business_type !== "mover" || mover.documents.length === 0)
    return null;

  return (
    <div id="documents" className="p-4 bg-white flex flex-col gap-4">
      <div className="flex flex-col ">
        <h2 className="text-lg font-medium">Documents</h2>
        <h4 className="text-sm font-normal text-stone-700">
          Important documents pertaining to your move
        </h4>
      </div>
      <div className="flex flex-col gap-1">
        <div className="font-normal font-mono">
          <ul className="space-y-1">
            {mover.documents.map((d) => (
              <li key={d.id}>
                <a
                  href={d.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium underline-offset-4 decoration-amber-500"
                >
                  {d.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const BillingArea = ({ move, mover }: { move: Move; mover: Mover }) => {
  if (move.status === "initial" || move.status === "contacting") return null;

  return (
    <div id="billing" className="p-4 bg-white flex flex-col gap-4">
      <h2 className="text-lg font-medium">Rates and Charges</h2>
      <div className="w-full md:w-[600px]">
        <RateCharges move={move} mover={mover} />
      </div>
    </div>
  );
};

const InventoryArea = ({ move, mover }: { move: Move; mover: Mover }) => {
  const [showFullInventory, setShowFullInventory] = useState(false);
  const MAX_INITIAL_ITEMS = 10;

  if (mover.business_type !== "mover") return null;

  return (
    <div id="inventory" className="bg-white p-4 flex flex-col gap-4">
      <h2 className="text-lg font-medium">Inventory</h2>
      <div className="w-full max-w-lg">
        {move.inventory_items && move.inventory_items.length > 0 && (
          <>
            <Table>
              <TableBody>
                {(showFullInventory
                  ? move.inventory_items
                  : move.inventory_items.slice(0, MAX_INITIAL_ITEMS)
                ).map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="pl-0">{item.name}</TableCell>
                    <TableCell className="text-right">
                      {item.quantity}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {move.inventory_items.length > MAX_INITIAL_ITEMS && (
              <div className="mt-2 text-center">
                {!showFullInventory ? (
                  <Button
                    variant="link"
                    className="p-0 h-auto text-sm text-black underline underline-offset-4 hover:text-black/80"
                    onClick={() => setShowFullInventory(true)}
                  >
                    Show More ({move.inventory_items.length - MAX_INITIAL_ITEMS}{" "}
                    more items)
                  </Button>
                ) : (
                  <Button
                    variant="link"
                    className="p-0 h-auto text-sm text-black underline underline-offset-4 hover:text-black/80"
                    onClick={() => setShowFullInventory(false)}
                  >
                    Show Less
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <Button
        onClick={() =>
          (window.location.href = `/hub/${move.identifier}/inventory`)
        }
        className="flex items-center gap-2 w-full max-w-md"
      >
        <PackageIcon className="w-4 h-4" />
        Update Inventory
      </Button>
    </div>
  );
};

const SignatureArea = ({
  move,
  mover,
  agreements,
}: {
  move: Move;
  mover: Mover;
  agreements?: Agreement[];
}) => {
  const [sigPad, setSigPad] = useState<SignatureCanvas | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [signAgreement] = useSignAgreementMutation();
  const paymentLinks = move?.payment_links;
  const unPaidDepositLink =
    (mover.mover_configuration?.deposit_link &&
      ({ full_url: mover.mover_configuration.deposit_link } as PaymentLink)) ||
    paymentLinks?.find((link) => link.purpose === "deposit" && !link.paid);
  const hasDepositRequirement = move.deposit_amount > 0 && unPaidDepositLink;

  if (!mover.mover_configuration.signed_estimates) {
    return null;
  }

  if (move.status !== "quoted") {
    return null;
  }

  const signature = agreements
    ?.find(
      (agreement: Agreement) =>
        agreement.kind === "estimate" && !agreement.voided_at
    )
    ?.signatures?.find((s: Signature) => s.signed_by === "customer");

  const handleSignatureSubmit = async () => {
    if (!sigPad || !move) return;

    if (sigPad.isEmpty()) {
      alert("Please sign the estimate");
      return;
    }

    setSubmitting(true);

    const signatureBlob = sigPad.toDataURL();

    try {
      await signAgreement({
        move_identifier: move.identifier,
        data: {
          kind: "estimate",
          signatures_attributes: [
            {
              name: move.customer_name,
              blob: signatureBlob,
              signed_by: "customer",
              position: "acceptance",
            },
          ],
        },
      });

      setSubmitting(false);
      setSubmitted(true);

      if (hasDepositRequirement) {
        window.location.href = unPaidDepositLink.full_url;
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (submitted) {
    return (
      <div className="py-4">
        <h2 className="text-lg font-medium mb-4">
          Thank you! We will confirm shortly.
        </h2>
      </div>
    );
  }

  if (signature) {
    return (
      <div className="py-4">
        <h2 className="text-lg font-medium mb-4">
          Thank you! We will confirm shortly.
        </h2>
        <SignatureDisplay signature={signature} label="Customer Signature" />
      </div>
    );
  }

  return (
    <div className="py-4">
      <h2 className="text-lg font-medium mb-2">Reserve Your Move</h2>
      <p className="mb-4">
        Please sign to accept the estimate and reserve your spot on our
        schedule.
      </p>
      <div className={cn("flex flex-col gap-2")}>
        <div className="flex-1 border border-stone-400">
          <SignatureCanvas
            ref={(ref) => setSigPad(ref)}
            canvasProps={{
              className: "w-full h-32",
            }}
            backgroundColor="white"
          />
        </div>
        <p className="text-xs text-stone-700">
          {move?.billing_type == "flat_rate"
            ? "I accept the charges in this estimate and agree to the terms and conditions listed below."
            : "I understand that this is an estimate based on the information provided and that the final amount may differ. I accept the rates and charges outlined and agree to the terms and conditions listed below."}
        </p>
        <div className="flex gap-2 mt-4">
          <Button
            variant="secondary"
            className="w-1/3"
            onClick={() => sigPad?.clear()}
          >
            Clear
          </Button>
          <Button
            disabled={submitting}
            className="w-3/4 bg-green-700 hover:bg-yellow-500 font-semibold py-4 text-white"
            onClick={async () => {
              await handleSignatureSubmit();
            }}
          >
            {submitting
              ? "Submitting..."
              : hasDepositRequirement
              ? "Continue to Deposit"
              : "Accept and Submit"}
          </Button>
        </div>
      </div>
    </div>
  );
};
